import React, { useState, useEffect } from 'react';
import { X, Copy, ExternalLink, Download, Edit2, Trash2 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { QRAnalyticsService } from '../../../services/qrAnalyticsService';
import { downloadBlob, downloadHref, downloadText } from '../../../utils/download';

export function QRCodeDetailsModal({ qrCode, isOpen, onClose, onEdit, onDelete }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('7d');
  const [qrPreview, setQrPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Load detailed analytics when modal opens
  useEffect(() => {
    if (!isOpen || !qrCode?.id) return;

    const loadAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        const days = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : timeframe === '90d' ? 90 : 7;
        const { data, error: analyticsError } = await QRAnalyticsService?.getQRCodeAnalytics(qrCode?.id, days);
        
        if (analyticsError) {
          setError(analyticsError);
          // Provide preview analytics for development
          setAnalytics({
            total_scans: qrCode?.totalScans || 45,
            unique_scans: Math.floor((qrCode?.totalScans || 45) * 0.8),
            conversions: Math.floor((qrCode?.totalScans || 45) * 0.15),
            conversion_rate: '13.3',
            device_breakdown: { 
              mobile: Math.floor((qrCode?.totalScans || 45) * 0.6),
              desktop: Math.floor((qrCode?.totalScans || 45) * 0.3),
              tablet: Math.floor((qrCode?.totalScans || 45) * 0.1)
            },
            country_breakdown: {
              'Netherlands': Math.floor((qrCode?.totalScans || 45) * 0.4),
              'Germany': Math.floor((qrCode?.totalScans || 45) * 0.3),
              'Belgium': Math.floor((qrCode?.totalScans || 45) * 0.2),
              'Other': Math.floor((qrCode?.totalScans || 45) * 0.1)
            },
            recent_scans: []
          });
          return;
        }

        setAnalytics(data);
      } catch (error) {
        console.log('Error loading QR code analytics:', error);
        setError('Failed to load analytics');
        setAnalytics(null);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [isOpen, qrCode?.id, timeframe]);

  // Generate styled QR code preview for modal - same logic as QRCodeCard
  const generateStyledQRPreview = async () => {
    if (!qrCode?.content || loadingPreview) return;
    
    setLoadingPreview(true);
    try {
      // Parse metadata for styling options
      let metadata = {};
      let styling = {};
      
      try {
        if (typeof qrCode?.metadata === 'string') {
          metadata = JSON.parse(qrCode?.metadata);
        } else if (qrCode?.metadata && typeof qrCode?.metadata === 'object') {
          metadata = qrCode?.metadata;
        }
        styling = metadata?.styling || {};
      } catch (parseError) {
        console.log('Metadata parsing failed, using defaults:', parseError);
        styling = {};
      }
      
      // Enhanced QR generation with styled pattern support
      const hasRoundedCorners = styling?.roundedCorners === true;
      const hasLogo = styling?.logoUrl;
      // Include dotStyle and cornerStyle in advanced styling detection
      const hasAdvancedStyling = hasRoundedCorners || hasLogo || 
                             styling?.dotStyle !== 'square' || 
                             styling?.cornerStyle !== 'square' ||
                             styling?.dotStyle === 'dots' ||
                             styling?.dotStyle === 'classy' ||
                             styling?.cornerStyle === 'extra-rounded' ||
                             styling?.cornerStyle === 'dot';
      
      if (hasAdvancedStyling) {
        try {
          // Use advanced QR generation for styled QR codes
          const QRCodeStyling = (await import('qr-code-styling'))?.default;
          
          const config = {
            width: 240,
            height: 240,
            data: qrCode?.content,
            margin: 10,
            qrOptions: {
              errorCorrectionLevel: styling?.errorCorrectionLevel || 'M'
            },
            dotsOptions: {
              color: styling?.foregroundColor || '#000000',
              // Properly map dotStyle including dots and classy
              type: styling?.dotStyle === 'dots' ? 'dots' : 
                    styling?.dotStyle === 'classy' ? 'classy-rounded' : 
                    styling?.dotStyle === 'rounded'? 'rounded' : hasRoundedCorners ?'rounded' : 'square'
            },
            backgroundOptions: {
              color: styling?.backgroundColor || '#ffffff'
            },
            cornersSquareOptions: {
              color: styling?.foregroundColor || '#000000',
              // Properly map cornerStyle including extra-rounded
              type: styling?.cornerStyle === 'extra-rounded' ? 'extra-rounded' : 
                    styling?.cornerStyle === 'dot' ? 'dot' : 
                    hasRoundedCorners ? 'extra-rounded' : 'square'
            },
            cornersDotOptions: {
              color: styling?.foregroundColor || '#000000',
              // Properly map corner dot style
              type: styling?.cornerStyle === 'extra-rounded' ? 'dot' : 
                    styling?.cornerStyle === 'dot' ? 'dot' : 
                    hasRoundedCorners ? 'dot' : 'square'
            }
          };

          // Add logo if present and valid
          if (styling?.logoUrl && typeof styling?.logoUrl === 'string') {
            config.image = styling?.logoUrl;
            config.imageOptions = {
              crossOrigin: "anonymous",
              margin: styling?.logoMargin || 4,
              imageSize: Math.min(40, Math.max(10, (styling?.logoSize || 20))) / 100,
              hideBackgroundDots: styling?.hideBackgroundDots !== false,
              excavate: styling?.excavate !== false
            };
          }

          const qrCodeStyling = new QRCodeStyling(config);
          
          // Generate styled QR code
          return new Promise((resolve, reject) => {
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.top = '-9999px';
            container.style.left = '-9999px';
            container.style.visibility = 'hidden';
            container.style.width = '240px';
            container.style.height = '240px';
            
            const cleanup = () => {
              try {
                if (container && container.parentNode) {
                  document.body.removeChild(container);
                }
              } catch (cleanupError) {
                console.log('Cleanup error (non-critical):', cleanupError);
              }
            };
            
            try {
              document.body.appendChild(container);
              qrCodeStyling.append(container);
              
              // Wait for rendering to complete, then extract the result
              setTimeout(() => {
                try {
                  const svgElement = container.querySelector('svg');
                  const canvasElement = container.querySelector('canvas');
                  
                  if (svgElement) {
                    try {
                      const svgData = new XMLSerializer().serializeToString(svgElement);
                      const svgBlob = new Blob([svgData], { type: 'image/svg+xml' });
                      const svgUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
                      cleanup();
                      resolve(svgUrl);
                    } catch (svgError) {
                      console.log('SVG serialization failed:', svgError);
                      cleanup();
                      reject(new Error('SVG serialization failed'));
                    }
                  } else if (canvasElement) {
                    try {
                      const dataUrl = canvasElement.toDataURL('image/png');
                      cleanup();
                      resolve(dataUrl);
                    } catch (canvasError) {
                      console.log('Canvas extraction failed:', canvasError);
                      cleanup();
                      reject(new Error('Canvas extraction failed'));
                    }
                  } else {
                    cleanup();
                    reject(new Error('No SVG or Canvas generated'));
                  }
                } catch (renderError) {
                  cleanup();
                  reject(renderError);
                }
              }, 500); // Increased timeout for modal rendering
              
            } catch (domError) {
              cleanup();
              reject(domError);
            }
          });
          
        } catch (advancedError) {
          console.log('Advanced QR generation failed, falling back:', advancedError);
          throw advancedError; // This will trigger the fallback below
        }
      } else {
        // Use basic QR generation for simple QR codes
        try {
          const QRCode = (await import('qrcode'))?.default;
          
          const canvas = document.createElement('canvas');
          canvas.width = 240;
          canvas.height = 240;
          
          await QRCode?.toCanvas(canvas, qrCode?.content, {
            width: 240,
            color: {
              dark: styling?.foregroundColor || '#000000',
              light: styling?.backgroundColor || '#ffffff'
            },
            errorCorrectionLevel: styling?.errorCorrectionLevel || 'M',
            margin: 4
          });
          
          const dataUrl = canvas?.toDataURL('image/png');
          return dataUrl;
        } catch (basicError) {
          console.log('Basic QR generation also failed:', basicError);
          throw basicError;
        }
      }
    } catch (error) {
      console.log('QR preview generation failed:', error);
      return null;
    } finally {
      setLoadingPreview(false);
    }
  };

  // Generate QR preview when modal opens
  useEffect(() => {
    if (isOpen && qrCode?.content && !qrPreview && !loadingPreview) {
      generateStyledQRPreview()?.then((result) => {
        if (result) {
          setQrPreview(result);
        }
      })?.catch((error) => {
        console.log('Error in QR preview generation:', error);
        setQrPreview(null);
      });
    }
  }, [isOpen, qrCode?.content, qrCode?.metadata]);

  // Copy functions
  const copyTrackableUrl = async () => {
    if (qrCode?.trackableUrl) {
      try {
        await navigator.clipboard?.writeText(qrCode?.trackableUrl);
        console.log('Trackable URL copied');
      } catch (error) {
        console.log('Failed to copy URL:', error);
      }
    }
  };

  const copyOriginalUrl = async () => {
    if (qrCode?.display_url) {
      try {
        await navigator.clipboard?.writeText(qrCode?.display_url);
        console.log('Original URL copied');
      } catch (error) {
        console.log('Failed to copy content:', error);
      }
    }
  };

  // Get QR content for rendering - prefer trackableUrl over content
  const getQRContent = () => {
    // IMPORTANT: the QR image must encode the TRACKABLE URL to record analytics.
    // After swapped-url migration, `qrCode.content` should contain the tracking URL,
    // while `display_url` is the destination URL shown to users.
    return qrCode?.content || qrCode?.trackableUrl || qrCode?.display_url || '';
  };

  // Get styling from metadata
  const getQRStyling = () => {
    try {
      let metadata = qrCode?.metadata;
      if (typeof metadata === 'string') {
        return JSON.parse(metadata)?.styling || {};
      }
      return metadata?.styling || {};
    } catch (error) {
      console.log('Error parsing QR styling metadata:', error);
      return {};
    }
  };

  // Download styled QR code (PNG or SVG)
  const handleDownloadQR = async (format = 'png') => {
    try {
      const safeFormat = format === 'svg' ? 'svg' : 'png';
      const fileNameBase = `${qrCode?.name || 'qr-code'}`.trim() || 'qr-code';
      const filename = `${fileNameBase}.${safeFormat}`;
      const qrContent = getQRContent();
      const styling = getQRStyling();
      
      // If SVG requested and we already have an SVG preview, download it directly.
      if (safeFormat === 'svg' && typeof qrPreview === 'string' && qrPreview.startsWith('data:image/svg')) {
        downloadHref(qrPreview, filename);
        return;
      }

      // Prefer generating a fresh download artifact with correct type.
      const hasRoundedCorners = styling?.roundedCorners === true;
      const hasLogo = styling?.logoUrl;
      const hasAdvancedStyling =
        hasRoundedCorners ||
        hasLogo ||
        styling?.dotStyle !== 'square' ||
        styling?.cornerStyle !== 'square' ||
        styling?.dotStyle === 'dots' ||
        styling?.dotStyle === 'classy' ||
        styling?.cornerStyle === 'extra-rounded' ||
        styling?.cornerStyle === 'dot';

      if (hasAdvancedStyling) {
        const QRCodeStyling = (await import('qr-code-styling'))?.default;

        const config = {
          width: 512,
          height: 512,
          type: safeFormat === 'svg' ? 'svg' : 'canvas',
          data: qrContent,
          margin: 10,
          qrOptions: {
            errorCorrectionLevel: styling?.errorCorrectionLevel || 'M'
          },
          dotsOptions: {
            color: styling?.foregroundColor || '#000000',
            type: styling?.dotStyle === 'dots' ? 'dots' :
                  styling?.dotStyle === 'classy' ? 'classy-rounded' :
                  styling?.dotStyle === 'rounded' ? 'rounded' : hasRoundedCorners ? 'rounded' : 'square'
          },
          backgroundOptions: {
            color: styling?.backgroundColor || '#ffffff'
          },
          cornersSquareOptions: {
            color: styling?.foregroundColor || '#000000',
            type: styling?.cornerStyle === 'extra-rounded' ? 'extra-rounded' :
                  styling?.cornerStyle === 'dot' ? 'dot' :
                  hasRoundedCorners ? 'extra-rounded' : 'square'
          },
          cornersDotOptions: {
            color: styling?.foregroundColor || '#000000',
            type: styling?.cornerStyle === 'extra-rounded' ? 'dot' :
                  styling?.cornerStyle === 'dot' ? 'dot' :
                  hasRoundedCorners ? 'dot' : 'square'
          }
        };

        if (styling?.logoUrl && typeof styling?.logoUrl === 'string') {
          config.image = styling?.logoUrl;
          config.imageOptions = {
            crossOrigin: "anonymous",
            margin: styling?.logoMargin || 4,
            imageSize: Math.min(40, Math.max(10, (styling?.logoSize || 20))) / 100,
            hideBackgroundDots: styling?.hideBackgroundDots !== false,
            excavate: styling?.excavate !== false
          };
        }

        const qrCodeStyling = new QRCodeStyling(config);

        if (qrCodeStyling?.getRawData) {
          const blob = await qrCodeStyling.getRawData(safeFormat);
          if (!blob) throw new Error('QR export returned no data');
          downloadBlob(blob, filename);
          return;
        }

        // Fallback: render and extract
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.top = '-9999px';
        container.style.left = '-9999px';
        container.style.visibility = 'hidden';
        document.body.appendChild(container);
        try {
          qrCodeStyling.append(container);
          await new Promise((r) => setTimeout(r, 250));
          if (safeFormat === 'svg') {
            const svgEl = container.querySelector('svg');
            if (!svgEl) throw new Error('No SVG generated');
            const svgData = new XMLSerializer().serializeToString(svgEl);
            downloadText(svgData, filename, 'image/svg+xml;charset=utf-8');
          } else {
            const canvasEl = container.querySelector('canvas');
            if (!canvasEl) throw new Error('No canvas generated');
            downloadHref(canvasEl.toDataURL('image/png'), filename);
          }
        } finally {
          document.body.removeChild(container);
        }
        return;
      }

      // Simple QR: use qrcode package for both PNG and SVG
      const QRCode = (await import('qrcode'))?.default;
      if (safeFormat === 'svg') {
        const svg = await QRCode?.toString(qrContent, {
          type: 'svg',
          errorCorrectionLevel: styling?.errorCorrectionLevel || 'M',
          margin: 4,
          color: {
            dark: styling?.foregroundColor || '#000000',
            light: styling?.backgroundColor || '#ffffff'
          }
        });
        downloadText(svg, filename, 'image/svg+xml;charset=utf-8');
      } else {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        await QRCode?.toCanvas(canvas, qrContent, {
          width: 512,
          color: {
            dark: styling?.foregroundColor || '#000000',
            light: styling?.backgroundColor || '#ffffff'
          },
          errorCorrectionLevel: styling?.errorCorrectionLevel || 'M',
          margin: 4
        });
        downloadHref(canvas.toDataURL('image/png'), filename);
      }
      
    } catch (error) {
      console.log('Download failed:', error);
    }
  };

  if (!isOpen) return null;

  const qrContent = getQRContent();
  let styling = getQRStyling();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {qrCode?.name || 'Unnamed QR Code'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {qrCode?.qr_type?.toUpperCase() || 'URL'} • Created {qrCode?.created_at ? new Date(qrCode?.created_at)?.toLocaleDateString() : 'Unknown'}
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* QR Code Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* QR Code Preview - Now shows styled QR codes */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">QR Code Preview</h3>
              <div className="w-full flex justify-center mb-4">
                <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-sm">
                  {loadingPreview ? (
                    <div className="w-60 h-60 flex items-center justify-center">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  ) : qrPreview ? (
                    <img
                      src={qrPreview}
                      alt="Styled QR Code Preview"
                      className="w-60 h-60 object-contain"
                      onError={() => {
                        console.log('Styled QR preview failed to load');
                        setQrPreview(null);
                      }}
                    />
                  ) : qrContent ? (
                    // Fallback to basic QR if styled generation fails
                    <div className="w-60 h-60 flex items-center justify-center bg-gray-50 rounded">
                      <div className="text-center text-gray-500">
                        <div className="text-sm">QR Preview Unavailable</div>
                        <div className="text-xs mt-1">Styled rendering failed</div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-60 h-60 flex items-center justify-center text-gray-400 bg-gray-50 rounded">
                      <div className="text-center">
                        <div className="text-sm">No content available</div>
                        <div className="text-xs mt-1">Cannot generate QR code</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Enhanced styling information display */}
              {qrContent && (
                <div className="text-center text-xs text-gray-600 mb-4">
                  <p className="truncate px-2" title={qrContent}>
                    Content: {qrContent}
                  </p>
                  <div className="flex justify-center items-center gap-4 text-xs mt-2">
                    <span>Size: 240x240px</span>
                    <span>Level: {styling?.errorCorrectionLevel || 'M'}</span>
                  </div>
                  
                  {/* Show applied styling information */}
                  {(styling?.dotStyle !== 'square' || styling?.cornerStyle !== 'square' || styling?.roundedCorners) && (
                    <div className="mt-2 text-blue-600">
                      <span className="font-medium">Applied Styling: </span>
                      {styling?.dotStyle && styling?.dotStyle !== 'square' && (
                        <span className="inline-block ml-1 px-2 py-0.5 bg-blue-100 rounded text-xs">
                          Dots: {styling?.dotStyle}
                        </span>
                      )}
                      {styling?.cornerStyle && styling?.cornerStyle !== 'square' && (
                        <span className="inline-block ml-1 px-2 py-0.5 bg-purple-100 rounded text-xs">
                          Corners: {styling?.cornerStyle}
                        </span>
                      )}
                      {styling?.roundedCorners && (
                        <span className="inline-block ml-1 px-2 py-0.5 bg-green-100 rounded text-xs">
                          Rounded
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(qrCode?.trackableUrl, '_blank')}
                  disabled={!qrCode?.trackableUrl}
                  className="w-full"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Trackable URL
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyTrackableUrl}
                  disabled={!qrCode?.trackableUrl}
                  className="w-full"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Trackable URL
                </Button>
              </div>
            </div>

            {/* Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className={`mt-1 px-2 py-1 rounded-full text-xs font-medium inline-block ${
                    qrCode?.statusColor === 'green' ?'bg-green-100 text-green-800' 
                      : qrCode?.statusColor === 'orange' ?'bg-orange-100 text-orange-800' :'bg-gray-100 text-gray-800'
                  }`}>
                    {qrCode?.statusText || 'Unknown'}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Original URL</label>
                  <div className="mt-1 flex items-center space-x-2">
                    <code className="flex-1 text-sm bg-gray-50 p-2 rounded border text-gray-800 truncate">
                      {qrCode?.display_url || 'No content'}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyOriginalUrl}
                      disabled={!qrCode?.display_url}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Trackable URL</label>
                  <div className="mt-1 flex items-center space-x-2">
                    <code className="flex-1 text-sm bg-gray-50 p-2 rounded border text-gray-800 truncate">
                      {qrCode?.trackableUrl || 'No trackable URL'}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyTrackableUrl}
                      disabled={!qrCode?.trackableUrl}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {qrCode?.campaigns?.name && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Campaign</label>
                    <p className="mt-1 text-sm text-gray-900">{qrCode?.campaigns?.name}</p>
                  </div>
                )}

                {qrCode?.expiry_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Expiry Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(qrCode?.expiry_date)?.toLocaleDateString()}
                    </p>
                  </div>
                )}

                {qrCode?.max_scans && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Scan Limit</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {qrCode?.currentScans || 0} / {qrCode?.max_scans} scans
                    </p>
                  </div>
                )}

                {/* Enhanced Styling Information */}
                {(styling?.foregroundColor || styling?.backgroundColor || styling?.dotStyle || styling?.cornerStyle) && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">QR Code Styling</label>
                    <div className="mt-2 space-y-2">
                      {(styling?.foregroundColor || styling?.backgroundColor) && (
                        <div className="flex items-center space-x-4">
                          {styling?.foregroundColor && (
                            <div className="flex items-center space-x-2">
                              <div 
                                className="w-4 h-4 rounded border"
                                style={{ backgroundColor: styling?.foregroundColor }}
                              ></div>
                              <span className="text-xs text-gray-600">Foreground</span>
                            </div>
                          )}
                          {styling?.backgroundColor && (
                            <div className="flex items-center space-x-2">
                              <div 
                                className="w-4 h-4 rounded border"
                                style={{ backgroundColor: styling?.backgroundColor }}
                              ></div>
                              <span className="text-xs text-gray-600">Background</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Pattern styling information */}
                      {(styling?.dotStyle !== 'square' || styling?.cornerStyle !== 'square') && (
                        <div className="text-xs text-gray-600">
                          {styling?.dotStyle && styling?.dotStyle !== 'square' && (
                            <div>Dot Pattern: <span className="font-medium">{styling?.dotStyle}</span></div>
                          )}
                          {styling?.cornerStyle && styling?.cornerStyle !== 'square' && (
                            <div>Corner Style: <span className="font-medium">{styling?.cornerStyle}</span></div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Analytics Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Analytics</h3>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e?.target?.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                <span className="ml-2 text-gray-600">Loading analytics...</span>
              </div>
            ) : error ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">Analytics temporarily unavailable</p>
                <p className="text-sm text-yellow-600 mt-1">Showing preview data for development</p>
              </div>
            ) : analytics ? (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Total Scans</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {analytics?.total_scans?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Unique Scans</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {analytics?.unique_scans?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Conversions</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {analytics?.conversions?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Conversion Rate</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {analytics?.conversion_rate ? `${analytics?.conversion_rate}%` : '0%'}
                    </p>
                  </div>
                </div>

                {/* Device Breakdown */}
                {analytics?.device_breakdown && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Device Breakdown</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {Object.entries(analytics?.device_breakdown)?.map(([device, count]) => (
                        <div key={device} className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-500 capitalize">{device}</p>
                          <p className="text-lg font-semibold text-gray-900">{count}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Country Breakdown */}
                {analytics?.country_breakdown && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Country Breakdown</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {Object.entries(analytics?.country_breakdown)?.slice(0, 8)?.map(([country, count]) => (
                        <div key={country} className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-500">{country}</p>
                          <p className="text-lg font-semibold text-gray-900">{count}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No analytics data available
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => handleDownloadQR('png')}
              disabled={loadingPreview}
            >
              <Download className="h-4 w-4 mr-2" />
              Download PNG
            </Button>

            <Button
              variant="outline"
              onClick={() => handleDownloadQR('svg')}
              disabled={loadingPreview}
            >
              <Download className="h-4 w-4 mr-2" />
              Download SVG
            </Button>
            
            <Button
              variant="outline"
              onClick={() => {
                onEdit?.();
                onClose();
              }}
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                onDelete?.(qrCode);
                onClose();
              }}
              className="text-red-600 border-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}